// Resources on the QEW Dashboard. Pure: SOP table and tasks in, page out.
// Words: RESOURCES_WORDS.md (approved 2026-10-06). Mockup: MOCKUP_RESOURCES.html (approved 2026-10-06).
//
// Rules:
// * Resources is general review. Reading here never qualifies anyone on a task.
// * ESWC (SOP-28.2) on top. Then Tier 1, 2, 3, collapsible, all closed.
// * Only SOPs for tasks switched on. Switching a task on brings its SOP back.
//   The task's own SOP number is the link, so the two can never drift.
// * The SOP page opens as it is today, with the acknowledgment hidden.
//   Same file as the qualifying read. Never a second copy.
// * Every open is logged in sopOpens: from Resources, the asset (Pick a Task) or the JSP.

import { TIER_SUB, type TaskDoc } from "./my-tasks";

/** One SOP as copied to sops/{sopNumber}. */
export interface SopDoc {
  sopNumber: string;
  title: string;
  fileUrl: string;
  version: string;
  minReadSeconds: number;
  requiresFullAck: boolean;
  /** True for the ESWC procedure. It covers every Tier 3 job, not one task. */
  isPermit?: boolean;
}

export interface SopEntry {
  sopNumber: string;
  /** "28.20", the chip on the row. */
  chip: string;
  title: string;
  fileUrl: string;
  version: string;
  /** The switched-on tasks this SOP covers, by name. */
  tasks: string[];
}

export interface SopTier {
  tier: 1 | 2 | 3;
  title: string;
  sub: string;
  sops: SopEntry[];
}

export interface SopLibrary {
  permit: SopEntry | null;
  tiers: SopTier[];
  total: number;
}

export const LIBRARY_FOOT = "Reference only. Qualify on a task at the equipment.";
export const REVIEW_LINE = "Reading here is for review. It does not qualify you on a task.";
export const SOP_FROM = ["Resources", "JSP", "Asset"] as const;
export type SopFrom = (typeof SOP_FROM)[number];

/** The two inspection program sheets, the same links as the live Resources tab. */
export const INSPECTION_PROGRAMS = [
  {
    name: "IR Thermography",
    line: "Opens the IR program sheet",
    url: "https://docs.google.com/spreadsheets/d/1fuOr0rSCUve_rOtSUcxTnddK-5uulkB_nOtdc42h8Bo/edit#gid=380827982",
  },
  {
    name: "Motor Lubrication",
    line: "Opens the lubrication tracking sheet",
    url: "https://docs.google.com/spreadsheets/d/1fuOr0rSCUve_rOtSUcxTnddK-5uulkB_nOtdc42h8Bo/edit#gid=1570915619",
  },
] as const;

export function sopChip(sopNumber: string): string {
  return sopNumber.replace(/^SOP-/, "");
}

function entryOf(s: SopDoc, tasks: string[]): SopEntry {
  return { sopNumber: s.sopNumber, chip: sopChip(s.sopNumber), title: s.title, fileUrl: s.fileUrl, version: s.version, tasks };
}

function taskOrder(id: string): number {
  const n = Number(id.replace(/\D/g, ""));
  return Number.isFinite(n) ? n : 9999;
}

/** The SOP Library: the permit on top, then each tier with the SOPs of its switched-on tasks. */
export function sopLibrary(sops: SopDoc[], tasks: TaskDoc[]): SopLibrary {
  const byNumber = new Map(sops.map((s) => [s.sopNumber, s]));
  const permitDoc = sops.find((s) => s.isPermit) ?? null;
  const on = tasks.filter((t) => t.isActive && t.sopNumber && byNumber.has(t.sopNumber)).sort((a, b) => taskOrder(a.taskId) - taskOrder(b.taskId));

  const tiers: SopTier[] = ([1, 2, 3] as const).map((tier) => {
    const seen = new Map<string, string[]>();
    for (const t of on) {
      if (t.tier !== tier) continue;
      const list = seen.get(t.sopNumber!) ?? [];
      list.push(t.name);
      seen.set(t.sopNumber!, list);
    }
    const entries = [...seen.entries()].map(([n, names]) => entryOf(byNumber.get(n)!, names));
    return { tier, title: `Tier ${tier}`, sub: TIER_SUB[tier], sops: entries };
  });
  const permit = permitDoc ? entryOf(permitDoc, []) : null;
  const total = tiers.reduce((n, t) => n + t.sops.length, 0) + (permit ? 1 : 0);
  return { permit, tiers, total };
}

/** Finds one SOP on the page by number. */
export function findSop(lib: SopLibrary, sopNumber: string): SopEntry | null {
  if (lib.permit?.sopNumber === sopNumber) return lib.permit;
  for (const t of lib.tiers) for (const s of t.sops) if (s.sopNumber === sopNumber) return s;
  return null;
}

/**
 * The SOP page for review. The same file, with the acknowledgment and the speed
 * banner hidden. A file without those IDs shows as it is, which is safe.
 */
export function reviewHtml(html: string): string {
  const css = "<style>#acknowledge{display:none!important}#cheatBanner{display:none!important}</style>";
  return html.includes("</head>") ? html.replace("</head>", `${css}</head>`) : css + html;
}

export function sopOpenProblem(sopNumber: unknown, from: unknown, jspNumber: unknown, assetId: unknown = null): string | null {
  if (typeof sopNumber !== "string" || !/^SOP-\d+(\.\d+[a-z]?)?$/.test(sopNumber)) return "Which SOP?";
  if (typeof from !== "string" || !(SOP_FROM as readonly string[]).includes(from)) return "Opened from where?";
  if (from === "JSP" && (typeof jspNumber !== "string" || !jspNumber.trim())) return "A JSP open needs the JSP number.";
  if (from === "Asset" && (typeof assetId !== "string" || !assetId.trim())) return "An open at the asset needs the asset ID.";
  return null;
}

/** One sopOpens record. Badge and name as text at the time of the open. */
export function sopOpenRecord(
  sopNumber: string,
  who: { badgeId: string; holderName: string },
  from: SopFrom,
  jspNumber: string | null,
  openedAt: string,
  assetId: string | null = null,
): Record<string, unknown> {
  return {
    sopNumber,
    qewId: who.badgeId,
    holderName: who.holderName,
    siteNumber: who.badgeId.slice(0, 2),
    from,
    jspNumber: from === "JSP" ? jspNumber : null,
    assetId: from === "Asset" ? assetId : null,
    openedAt,
  };
}
